import React, { Component } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { INVOICE_FEE_TYPES } from "@constants";
import { InvoiceService } from "@services";
import utils from "@utils";
import Modal from "../Modal/Modal";
import Editable from "./components/EditableItem";

import "./EditInvoiceModal.scss";

export class EditInvoiceModal extends Component {
	constructor(props) {
		super(props);

		this.initialState = {
			isOpen: false,
			invoiceItems: [],
			officerName: "",
			jobDate: "",
			clockIn: "",
			clockOut: "",
			expandOptions: false,
			creating: false,
			creatingType: "",
			_newItem: {
				ItemName: "",
				ItemTypeId: "",
				Price: 0,
				Quantity: 0,
				_id: "",
				_editing: false,
			},
			updatingInvoice: false,
		};

		this.state = { ...this.initialState };
	}

	findById = (invoiceItems, id) => {
		let found;

		found = invoiceItems.findIndex(item => item.Id === id);

		// if the ID is not found, it's because is new so we search the temporal Id
		if (found === -1) {
			found = invoiceItems.findIndex(item => item._id === id);
		}

		return found;
	};

	/** Opens the modal and retrieves invoice data using the `invoiceId` props. This method is currently called via a `ref` to the modal. */
	open = () => {
		const {
			invoiceId,
			user: { TimeZoneId },
			user,
		} = this.props;
		InvoiceService.getInvoiceById(invoiceId)
			.then(({ data }) => {
				const rawItems = [
					...(data.Items || []),
					...(data.JurisdictionFees || []),
					data.CoordinatorFees,
					data.JurisdictionVehicleFees,
				];
				this.setState(
					{
						officerName: data.OfficerName,
						jobDate: utils.formatDate(
							`${data.JobStartDateTime}Z`,
							"en-US",
							TimeZoneId,
							"long",
						),
						clockIn: utils.formatTime(
							new Date(`${data.ClockInDateTime}Z`),
							user.UserPreferences !== "useMilitaryTime",
							TimeZoneId,
						),
						clockOut: utils.formatTime(
							new Date(`${data.ClockOutDateTime}Z`),
							user.UserPreferences !== "useMilitaryTime",
							TimeZoneId,
						),
						invoiceItems: rawItems.filter(item => item !== null && item !== undefined),
					},
					() => this.setState({ isOpen: true }),
				);
			})
			.catch(() => {});
	};

	close = () => {
		this.setState(this.initialState);
	};

	/**
	 * Identifies which invoice item is being edited and updates that line item with the most current values
	 * @param {Object} item The invoice line item that was updated
	 */
	edit = ({ _id, Id, ItemTypeId, ItemName, Price, Quantity, ServiceChargeTypeId }) => {
		const { invoiceItems: rawItems } = this.state;
		let invoiceItems = [...rawItems];
		const found = this.findById(invoiceItems, Id || _id);

		invoiceItems[found] = {
			...invoiceItems[found],
			ItemTypeId,
			ItemName,
			Price,
			Quantity,
			ServiceChargeTypeId,
		};

		// If we have just edited the timecard, make sure the hourly and percentage based fees are also updated
		// Flat fees have a calculation type id of 1, so anything that isn't one should be updated accordingly
		if (ItemTypeId === 1) {
			invoiceItems = invoiceItems.map(item => {
				const rawItem = rawItems[found];
				const percent = parseFloat(item.Price) / parseFloat(rawItem.Total);
				switch (item.CalculationType) {
					case 3: // Percentage (quantity is currently one for these fee types)
						return {
							...item,
							Price: parseFloat(
								(percent * parseFloat(Price) * parseFloat(Quantity)).toFixed(2),
							),
							Total: parseFloat(
								(percent * parseFloat(Price) * parseFloat(Quantity)).toFixed(2),
							),
							_changed: true,
						};
					case 2: // Hourly (includes timecard)
						return {
							...item,
							Quantity: parseFloat(parseFloat(Quantity).toFixed(2)),
							Total: parseFloat(
								(parseFloat(item.Price) * parseFloat(Quantity)).toFixed(2),
							),
							_changed: !ItemTypeId === 1,
						};
					case 1: // Flat rate
					default:
						return { ...item, _changed: false };
				}
			});
		}
		this.setState({ invoiceItems }, () => {
			this.toggleEdit(Id || _id);
			// Wait .75s to remove the class 'changed' from any element that currently has it
			// If the scss animation time changes, this value must also be updated
			setTimeout(() => {
				this.setState({
					invoiceItems: invoiceItems.map(item => ({ ...item, _changed: false })),
				});
				clearTimeout();
			}, 750);
		});
	};

	/**
	 * Saves changes to invoice items. If there are any items currently being created or edited,
	 * the user will be asked to save those changes locally before proceeding.
	 */
	save = () => {
		const { lang } = global;
		const { invoiceId: id, onSave } = this.props;
		const { creating } = this.state;
		const {
			invoiceItems: [...invoiceItems],
		} = this.state;
		let editing = false;

		invoiceItems.forEach(({ _editing }) => {
			if (_editing) editing = true;
		});

		if (creating || editing) {
			utils.notify({
				type: "warning",
				message: lang.t("Save or discard your changes to the current New Fee"),
			});
			this.setState({ expandOptions: false });
		} else {
			const data = invoiceItems.map(rawItem => {
				const { ItemName, ItemTypeId, Price, Quantity, ServiceChargeTypeId } = rawItem;
				const item = {
					ItemName,
					ItemTypeId,
					Price,
					Quantity,
				};

				if (rawItem.Id) item.Id = rawItem.Id;
				if (ItemTypeId === INVOICE_FEE_TYPES.JURISDICTION_VEHICLE.value)
					item.ServiceChargeTypeId = ServiceChargeTypeId;

				return item;
			});
			this.setState({ updatingInvoice: true });
			InvoiceService.updateInvoiceItems({ id, data })
				.then(() => {
					utils.notify({
						type: "success",
						message: lang.t("Invoice updated successfully"),
					});
					this.close();
					onSave();
				})
				.catch(() => {
					this.setState({ updatingInvoice: false });
					utils.notify({
						type: "error",
						message: lang.t("Error updating invoice"),
					});
				});
		}
	};

	/**
	 * Displays the section to create a mew item to be added to the invoice.
	 * If there are any items currently being created or edited, the user will be asked to save those changes locally before proceeding.
	 * @param {Object} obj
	 * @param {String} obj.label A description of what type of item will be created
	 * @param {Number} obj.value The ID number of the invoice item type to be created
	 */
	openCreation = ({ label: creatingType, value: ItemTypeId }) => {
		const { lang } = global;
		const { creating, invoiceItems } = this.state;

		let editing = false;

		invoiceItems.forEach(({ _editing }) => {
			if (_editing) editing = true;
		});

		if (creating || editing) {
			utils.notify({
				type: "warning",
				message: lang.t("Save or discard your changes to the current New Fee"),
			});
			this.setState({ expandOptions: false });
		} else {
			this.setState(prevState => ({
				expandOptions: false,
				creating: true,
				creatingType,
				_newItem: {
					...prevState._newItem,
					ItemTypeId,
				},
			}));
		}
	};

	/** Discards the line to create an invoice item that is currently open */
	discardCreation = () => {
		const { creating, creatingType, _newItem } = this.initialState;
		this.setState({ creating, creatingType, _newItem });
	};

	/**
	 * Saves all changes to the invoice item that is currently being created
	 * @param {Object} item The invoice item to be saved locally
	 */
	create = ({ ItemName, ItemTypeId, Price, Quantity }) => {
		const { invoiceItems: rawItems } = this.state;
		const invoiceItems = [...rawItems];

		// when creating new item, use a timestamp as temporal identifier.
		invoiceItems.push({
			ItemName,
			ItemTypeId,
			Price,
			Quantity,
			_id: Date.now(),
			_editing: false,
		});

		this.setState({ invoiceItems, creating: false });
	};

	/**
	 * Deletes an item from the invoice
	 * @param {Number|String} id An identifier for the invoice item
	 */
	delete = id => {
		const { invoiceItems: rawItems } = this.state;
		const invoiceItems = [...rawItems];
		const found = this.findById(invoiceItems, id);

		invoiceItems.splice(found, 1);

		this.setState({ invoiceItems });
	};

	toggleDropdown = () =>
		this.setState(prevState => ({ expandOptions: !prevState.expandOptions }));

	toggleEdit = id => {
		const { invoiceItems: rawInvoiceItems } = this.state;
		const invoiceItems = [...rawInvoiceItems];
		const toggledItem = this.findById(invoiceItems, id);

		invoiceItems[toggledItem]._editing = !invoiceItems[toggledItem]._editing;

		this.setState({ invoiceItems });
	};

	/**
	 * Renders an invoice item in a view-only format with options to edit or delete as desired
	 * @param {Object} item The invoice item to be shown
	 */
	renderItem = item => {
		const { lang } = global;
		return (
			<div
				key={item.Id || item._id}
				className="row invoice-item form-group"
			>
				<div className="col-sm-3">
					<span className="visible-xs">
						<small>{lang.t("Item Name")}</small>
					</span>
					{item.ItemName}
				</div>
				<div className="col-xs-4 col-sm-2">
					<span className="visible-xs">
						<small>{lang.t("Rate")}</small>
					</span>
					{utils.currencyFormatter(item.Price)}
				</div>
				<div className="col-xs-4 col-sm-2">
					<span className="visible-xs">
						<small>{lang.t("Quantity")}</small>
					</span>
					{item.Quantity}
				</div>
				<div className={classNames("col-xs-4 col-sm-1", { changed: item._changed })}>
					<span className="visible-xs">
						<small>{lang.t("Total")}</small>
					</span>
					{utils.currencyFormatter(item.Price * item.Quantity)}
				</div>
				<div className="col-xs-6 col-sm-2 spacing-top">
					<button
						type="button"
						className="btn btn-primary btn-block"
						onClick={() => this.toggleEdit(item?.Id || item._id)}
					>
						<span>{lang.t("Edit")}</span>
					</button>
				</div>
				<div className="col-xs-6 col-sm-2 spacing-top">
					<button
						type="button"
						className="btn btn-danger btn-block"
						onClick={() => this.delete(item?.Id || item._id)}
						disabled={item.ItemTypeId === INVOICE_FEE_TYPES.TIMECARD.value}
					>
						{lang.t("Delete")}
					</button>
				</div>
			</div>
		);
	};

	render() {
		const { lang } = global;
		const { user } = this.props;
		const {
			isOpen,
			invoiceItems,
			officerName,
			jobDate,
			clockIn,
			clockOut,
			expandOptions,
			creating,
			creatingType,
			_newItem,
			updatingInvoice,
		} = this.state;

		const officerFees = invoiceItems.filter(
			item => item.ItemTypeId === INVOICE_FEE_TYPES.TIMECARD.value,
		);
		const organizationFees = invoiceItems.filter(
			item => item.ItemTypeId === INVOICE_FEE_TYPES.ORGANIZATION.value,
		);
		const jurisdictionFees = invoiceItems.filter(
			item => item.ItemTypeId === INVOICE_FEE_TYPES.JURISDICTION.value,
		);
		const jurisdictionAssets = invoiceItems.filter(
			item => item.ItemTypeId === INVOICE_FEE_TYPES.JURISDICTION_ASSET.value,
		);
		const jurisdictionVehicleFees = invoiceItems.filter(
			item => item.ItemTypeId === INVOICE_FEE_TYPES.JURISDICTION_VEHICLE.value,
		);
		const miscellaneousFees = invoiceItems.filter(
			item => item.ItemTypeId === INVOICE_FEE_TYPES.MISCELLANEOUS.value,
		);

		return (
			<Modal
				show={isOpen}
				onHide={this.close}
				id="edit-invoice-modal"
				size="lg"
			>
				<Modal.Header>
					<Modal.Title className="text-left">{`${lang.t("Edit Invoice For")} ${officerName}`}</Modal.Title>
					<small>
						{jobDate}, {clockIn} - {clockOut}
					</small>
				</Modal.Header>
				<Modal.Body>
					<div className="row hidden-xs">
						<div className="col-xs-3">
							<strong>{lang.t("Item Name")}</strong>
						</div>
						<div className="col-xs-2">
							<strong>{lang.t("Rate")}</strong>
						</div>
						<div className="col-xs-2">
							<strong>{lang.t("Quantity")}</strong>
						</div>
						<div className="col-xs-1">
							<strong>{lang.t("Total")}</strong>
						</div>
						<div className="col-xs-4" />
					</div>
					<div className="separator separator-solid hidden-xs" />
					<For
						each="officerFee"
						of={officerFees}
					>
						<Choose>
							<When condition={officerFee._editing}>
								<Editable
									key={`officerFee-${officerFee.Id || officerFee._id}`}
									item={officerFee}
									onCancel={() =>
										this.toggleEdit(officerFee?.Id || officerFee._id)
									}
									onSave={this.edit}
								/>
							</When>
							<Otherwise>{this.renderItem(officerFee)}</Otherwise>
						</Choose>
					</For>
					<If condition={miscellaneousFees.length > 0}>
						<small>
							<strong>{lang.t("Officer Miscellaneous Items")}</strong>
						</small>
						<For
							each="miscFee"
							of={miscellaneousFees}
						>
							<Choose>
								<When condition={miscFee._editing}>
									<Editable
										key={`miscFee-${miscFee.Id || miscFee._id}`}
										item={miscFee}
										onCancel={() => this.toggleEdit(miscFee?.Id || miscFee._id)}
										onSave={this.edit}
									/>
								</When>
								<Otherwise>{this.renderItem(miscFee)}</Otherwise>
							</Choose>
						</For>
					</If>
					<If condition={organizationFees.length > 0}>
						<small>
							<strong>{lang.t("Organization Fees")}</strong>
						</small>
						<For
							each="organizationFee"
							of={organizationFees}
						>
							<Choose>
								<When condition={organizationFee._editing}>
									<Editable
										key={`organizationFee-${organizationFee?.Id || organizationFee?._id}`}
										item={organizationFee}
										onCancel={() =>
											this.toggleEdit(
												organizationFee?.Id || organizationFee?._id,
											)
										}
										onSave={this.edit}
									/>
								</When>
								<Otherwise>{this.renderItem(organizationFee)}</Otherwise>
							</Choose>
						</For>
					</If>
					<If condition={jurisdictionFees.length > 0}>
						<small>
							<strong>{lang.t("Jurisdiction Fees")}</strong>
						</small>
						<For
							each="jurisdictionFee"
							of={jurisdictionFees}
						>
							<Choose>
								<When condition={jurisdictionFee._editing}>
									<Editable
										key={`jurisdictionFee-${jurisdictionFee.Id || jurisdictionFee._id}`}
										item={jurisdictionFee}
										onCancel={() =>
											this.toggleEdit(
												jurisdictionFee?.Id || jurisdictionFee._id,
											)
										}
										onSave={this.edit}
									/>
								</When>
								<Otherwise>{this.renderItem(jurisdictionFee)}</Otherwise>
							</Choose>
						</For>
					</If>
					<If condition={jurisdictionAssets.length > 0}>
						<small>
							<strong>{lang.t("Assets / Equipment")}</strong>
						</small>
						<For
							each="jurisdictionAsset"
							of={jurisdictionAssets}
						>
							<Choose>
								<When condition={jurisdictionAsset._editing}>
									<Editable
										key={`jurisdictionAsset-${jurisdictionAsset.Id || jurisdictionAsset._id}`}
										item={jurisdictionAsset}
										onCancel={() =>
											this.toggleEdit(
												jurisdictionAsset?.Id || jurisdictionAsset._id,
											)
										}
										onSave={this.edit}
									/>
								</When>
								<Otherwise>{this.renderItem(jurisdictionAsset)}</Otherwise>
							</Choose>
						</For>
					</If>
					<If condition={jurisdictionVehicleFees.length > 0}>
						<small>
							<strong>{lang.t("Jurisdiction Vehicle Fees")}</strong>
						</small>
						<For
							each="jurisdictionVehicleFee"
							of={jurisdictionVehicleFees}
						>
							<Choose>
								<When condition={jurisdictionVehicleFee._editing}>
									<Editable
										key={`jurisdictionVehicleFee-${jurisdictionVehicleFee.Id || jurisdictionVehicleFee._id}`}
										item={jurisdictionVehicleFee}
										onCancel={() =>
											this.toggleEdit(
												jurisdictionVehicleFee?.Id ||
													jurisdictionVehicleFee._id,
											)
										}
										onSave={this.edit}
									/>
								</When>
								<Otherwise>{this.renderItem(jurisdictionVehicleFee)}</Otherwise>
							</Choose>
						</For>
					</If>
					<If condition={creating}>
						<small>
							{lang.t("New")} {creatingType}
						</small>
						<div className="row hidden-xs">
							<div className="col-sm-3">
								<strong>{lang.t("Item Name")}</strong>
							</div>
							<div className="col-xs-4 col-sm-2">
								<strong>{lang.t("Rate")}</strong>
							</div>
							<div className="col-xs-4 col-sm-2">
								<strong>{lang.t("Quantity")}</strong>
							</div>
							<div className="col-xs-4 col-sm-1">
								<strong>{lang.t("Total")}</strong>
							</div>
						</div>
						<Editable
							item={_newItem}
							onCancel={this.discardCreation}
							onSave={this.create}
						/>
					</If>
					<div className="row">
						<div className="col-sm-3 col-sm-offset-9 text-right">
							<div
								className={classNames("dropdown btn-group btn-group-link", {
									open: expandOptions,
								})}
							>
								<button
									type="button"
									className="btn btn-link btn-block dropdown-toggle"
									onClick={this.toggleDropdown}
								>
									{`+ ${lang.t("Add Fee")}`}
									&nbsp;
									<span className="caret" />
								</button>
								<ul
									role="menu"
									className="dropdown-menu"
								>
									<li role="presentation">
										<button
											type="button"
											className="btn btn-link btn-block text-left"
											onClick={() =>
												this.openCreation(INVOICE_FEE_TYPES.MISCELLANEOUS)
											}
										>
											{INVOICE_FEE_TYPES.MISCELLANEOUS.label}
										</button>
									</li>
									<If condition={user.isSuperAdmin()}>
										<li role="presentation">
											<button
												type="button"
												className="btn btn-link btn-block text-left"
												onClick={() =>
													this.openCreation(
														INVOICE_FEE_TYPES.ORGANIZATION,
													)
												}
												disabled={organizationFees.length >= 1}
											>
												{INVOICE_FEE_TYPES.ORGANIZATION.label}
											</button>
										</li>
									</If>
									<li role="presentation">
										<button
											type="button"
											className="btn btn-link btn-block text-left"
											onClick={() =>
												this.openCreation(
													INVOICE_FEE_TYPES.JURISDICTION_ASSET,
												)
											}
										>
											{INVOICE_FEE_TYPES.JURISDICTION_ASSET.label}
										</button>
									</li>
									<li role="presentation">
										<button
											type="button"
											className="btn btn-link btn-block text-left"
											onClick={() =>
												this.openCreation(INVOICE_FEE_TYPES.JURISDICTION)
											}
										>
											{INVOICE_FEE_TYPES.JURISDICTION.label}
										</button>
									</li>
									<li role="presentation">
										<button
											type="button"
											className="btn btn-link btn-block text-left"
											onClick={() =>
												this.openCreation(
													INVOICE_FEE_TYPES.JURISDICTION_VEHICLE,
												)
											}
											disabled={jurisdictionVehicleFees.length >= 1}
										>
											{INVOICE_FEE_TYPES.JURISDICTION_VEHICLE.label}
										</button>
									</li>
								</ul>
							</div>
						</div>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<div className="row">
						<div className="col-xs-6 col-sm-3 col-sm-offset-3">
							<button
								type="button"
								className="btn btn-primary btn-block"
								onClick={this.save}
							>
								<Choose>
									<When condition={updatingInvoice}>
										<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
									</When>
									<Otherwise>{lang.t("Save")}</Otherwise>
								</Choose>
							</button>
						</div>
						<div className="col-xs-6 col-sm-3">
							<button
								type="button"
								className="btn btn-link btn-block"
								onClick={this.close}
							>
								{lang.t("Cancel")}
							</button>
						</div>
					</div>
				</Modal.Footer>
			</Modal>
		);
	}
}

EditInvoiceModal.propTypes = {
	invoiceId: PropTypes.number,
	user: PropTypes.object,
	onSave: PropTypes.func.isRequired,
};

EditInvoiceModal.defaultProps = {
	invoiceId: undefined,
	user: {},
};

export default EditInvoiceModal;
